import React, { useState } from 'react';

import StepHeader from '../../design-system/StepHeader';
import Button from '../../design-system/Button';
import { spacing } from '../../design-system/tokens';
import OrderSummaryCard from './OrderSummaryCard';
import type { OrderSummaryItem } from './OrderSummaryCard';

/**
 * Resumo do pedido — step do fluxo de abastecimento (por local).
 *
 * Composite de NEGÓCIO (não pertence ao DS): monta `StepHeader` + o card
 * reutilizável `OrderSummaryCard` (Panel + DescriptionRow) + rodapé com `Button`.
 *
 * Multi-local: percorre `locations`; "Ir para o próximo local" avança e, no
 * último, o botão vira "Finalizar pedido(s)" e dispara `onComplete` (que, no
 * fluxo, leva ao "Resumo geral do pedido").
 */

export type { OrderSummaryItem } from './OrderSummaryCard';

export interface LocationSummary {
  id: string;
  /** Título do cabeçalho (ex.: "Refeitório fundos"). */
  title: string;
  /** Subtítulo (ex.: "preencha os dados abaixo"). */
  subtitle?: string;
  /** Itens do resumo, em ordem. */
  items: OrderSummaryItem[];
}

export interface OrderReviewSectionProps {
  /** Locais a revisar (a "base"). */
  locations: LocationSummary[];
  /** Índice inicial (controlado por stories/testes). @default 0 */
  initialIndex?: number;
  /** Ação do botão "Editar dados do local". */
  onEditLocation?: (id: string) => void;
  /** Disparado ao avançar para o próximo local. */
  onNextLocation?: (id: string) => void;
  /** Disparado no último local ("Finalizar pedido(s)" → resumo geral). */
  onComplete?: () => void;
  /** Ação do botão "Alterar central de abastecimento". */
  onChangeLocations?: () => void;
}

// ── Ícones de domínio (vivem no feature, não no átomo) ──────────────────────

const PinIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const CylinderIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 3h6v2.5H9z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <rect
      x="8"
      y="5.5"
      width="8"
      height="15.5"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M8 10h8" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const CalendarIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3.5"
      y="5"
      width="17"
      height="15"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M3.5 9.5h17M8 3.5v3M16 3.5v3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PersonIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/** Ícones default por campo (definidos no feature). */
export const ORDER_SUMMARY_ICONS = {
  local: <PinIcon />,
  gas: <CylinderIcon />,
  delivery: <CalendarIcon />,
  responsible: <PersonIcon />,
};

export const OrderReviewSection = ({
  locations,
  initialIndex = 0,
  onEditLocation,
  onNextLocation,
  onComplete,
  onChangeLocations,
}: OrderReviewSectionProps): JSX.Element => {
  const [index, setIndex] = useState(initialIndex);
  const total = locations.length;
  const current = locations[Math.min(index, total - 1)];
  const isLast = index >= total - 1;

  const handleNext = (): void => {
    if (isLast) {
      onComplete?.();
      return;
    }
    onNextLocation?.(current.id);
    setIndex((i) => Math.min(i + 1, total - 1));
  };

  if (!current) return <div />;

  const finishLabel = total > 1 ? 'Finalizar pedidos' : 'Finalizar pedido';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      {/* Ação no topo da página */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="secondary" shape="pill" onClick={onChangeLocations}>
          Alterar central de abastecimento
        </Button>
      </div>

      {/* Cabeçalho do local + contador */}
      <StepHeader
        title={current.title}
        description={current.subtitle}
        state="active"
        counter={total > 1 ? `Local ${index + 1} de ${total}` : undefined}
      />

      {/* Card "Resumo do pedido" (reutilizável) */}
      <OrderSummaryCard title="Resumo do pedido" items={current.items} />

      {/* Rodapé de ações */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: spacing.md,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="secondary"
          shape="pill"
          size="xl"
          onClick={() => onEditLocation?.(current.id)}
        >
          Editar dados do local
        </Button>
        <Button variant="primary" shape="pill" size="xl" onClick={handleNext}>
          {isLast ? finishLabel : 'Ir para a próxima central de abastecimento'}
        </Button>
      </div>
    </div>
  );
};

export default OrderReviewSection;
